<template>
	<Skeleton :loading="loading">
		<!-- Loading template -->
		<template #loading>
			<div id="restaurant_detail">
				<h4 
					class="text-heading2--bold pa--4 my--0"
				>
					{{ $t('pageRestaurant.nearbranchdata') }}
				</h4>
				<el-row class="px--4 d-flex">
					<el-col :span="7">
						<div class="color-gray95" style="border-radius: 6px; width: 85%; height: 100%" />
					</el-col>
					<el-col :span="14">
						<SkeletonItem variant="heading3" style="width: 95%;" />
						<SkeletonItem class="mt--1" variant="body" style="width: 95%;" />
						<SkeletonItem class="mt--1" variant="body" style="width: 30%;" />
					</el-col>
					<el-col :span="3"	class="text-xs-center self-ys-center">
						<SkeletonItem variant="body" style="width: 90%;" />
					</el-col>
				</el-row>
				<div class="px--4">
					<div class="contact-item" >
						<img src="@/assets/icons/phone.png" class="icon-contact">
						<SkeletonItem class="vertical-middle" variant="body" style="width: 35%;" />
					</div>
					<div class="contact-item" >
						<img src="@/assets/icons/clock.png" class="icon-contact">
						<SkeletonItem class="vertical-middle" variant="body" style="width: 35%;" />
					</div>
					<div class="contact-item" >
						<img src="@/assets/icons/thank/messenger.png" class="icon-contact">
						<SkeletonItem class="vertical-middle" variant="body" style="width: 70%;" />
					</div>
					<div class="contact-item" >
						<img src="@/assets/icons/thank/line.png" class="icon-contact">
						<SkeletonItem class="vertical-middle" variant="body" style="width: 35%;" />
					</div>
					<div class="contact-item">
						<img src="@/assets/icons/moreInfo.png" class="icon-contact">
						<span class="text-subtitle--bold">{{ $t('pageRestaurant.moreInfo') }}</span><br>
						<SkeletonItem class="ml--8 mt--2" variant="body" style="width: 35%;" />
					</div>
				</div>
			</div>
		</template>

		<!-- Content template -->
		<template>
			<div 
				v-if="!nearbyBranch" 
				class="pa--4 text-xs-center" 
			>
				<h1 class="text-heading">The restaurant doesn't have an online branch</h1>
			</div>
			<div id="restaurant_detail" v-else>
				<h4 
					class="text-heading2--bold pa--4 my--0"
				>
					{{ $t('pageRestaurant.nearbranchdata') }}
				</h4>
				<el-row class="px--4 d-flex" v-if="branchPosition">
					<el-col :span="7" class="r-icon-map">
						<div
							class="p-relative full-height color-gray95" 
							style="min-height: 90px; border-radius: 8px;"
						>
							<Gmap
								:position="branchPosition" 
								:marker-icon="require('@/assets/icons/svg/pin.svg')"
            		:marker-size="{ height: 45, width: 45 }"
								:readonly="true"
								:zoom="16"
							/>
							<div 
								v-if="Number.isFinite(nearbyBranch.distance)"
								class="color-white color-charcoal--text py--0 px--1 text-body--med p-absolute topleft-5" 
								style="border-radius: 4px;"
							>
								{{ nearbyBranch.distance ? (nearbyBranch.distance / 1000).toFixed(1) : 0 }} {{ $t('global.kilometer') }}
							</div>
						</div>
					</el-col>
					<el-col :span="14">
						<label class="fs-wrp text-heading2--med">{{ nearbyBranch.name }}</label>
						<label class="text-grey text-body mt--1">{{ nearbyBranch.address }}</label>
					</el-col>
					<el-col 
						:span="3" 
						class="text-xs-center self-ys-center" 
					>
						<a :href="googleMapLink" target="_blank">
							<img 
								:src="require('@/assets/icons/svg/direction-circle.svg')"
								alt="google-map-link"
							/>
						</a>
					</el-col>
				</el-row>
				<div class="px--4">
						<div 
							class="contact-item" 
							v-if="nearbyBranch.phone"
							@click="openContact(nearbyBranch.phone, 'tel')"
						>
							<img src="@/assets/icons/phone.png" class="icon-contact">
							<span class="text-heading3 vertical-middle">
								{{ toTelephoneNumber(nearbyBranch.phone) }}
							</span>
						</div>
						<div class="contact-item" v-if="deliveryTime.isOpen">
								<img src="@/assets/icons/clock.png" class="icon-contact">
								<span
										class="text-grey text-heading3 vertical-middle"
										v-if="nextDeliveryTime.dayId != 99">
										<b class="font-green">{{ $t('pageMenuList.header.open') }}</b> {{ $t('pageMenuList.header.to') }} {{deliveryTime.closeTime}}
								</span>
								<span class="text-grey" v-else><b class="font-green">{{ $t('pageMenuList.header.open') }}</b></span>
						</div>
						<div class="contact-item" v-else>
								<img src="@/assets/icons/clock.png" class="icon-contact">
								<span
										class="text-grey text-heading3 vertical-middle"
										v-if="deliveryTime.overTimeFlag">
										<b class="font-red">{{ $t('pageMenuList.header.close') }}</b> {{ $t('pageMenuList.header.to') }} {{ deliveryTime.timePeriod[0].openTime }}
								</span>
								<span
										class="text-grey text-heading3 vertical-middle"
										v-else-if="!deliveryTime.overTimeFlag && nextDeliveryTime.dayId != 99 && $i18n.locale === 'en'">
										<b class="font-red">{{ $t('pageMenuList.header.close') }}</b> {{ $t('pageMenuList.header.to') }} {{ nextDeliveryTime.dayId | filterDayEN }} {{nextDeliveryTime.openTime}}
								</span>
								<span
										class="text-grey text-heading3 vertical-middle"
										v-else-if="!deliveryTime.overTimeFlag && nextDeliveryTime.dayId != 99 && $i18n.locale !== 'en'">
										<b class="font-red">{{ $t('pageMenuList.header.close') }}</b> {{ $t('pageMenuList.header.to') }} {{ nextDeliveryTime.dayId | filterDayTH }} {{nextDeliveryTime.openTime}}
								</span>
								<span class="text-grey text-heading3 vertical-middle" v-else><b class="font-red">{{ $t('pageMenuList.header.close') }}</b></span>
						</div>
						<div 
							class="contact-item text-body" 
							v-if="nearbyBranch.fb_msg_url" 
							@click="openContact(nearbyBranch.fb_msg_url, 'facebook')"
						>
							<!-- <img src="@/assets/icons/thank/messenger.png" class="icon-contact"> -->
          		<img class="icon-contact" :src="require('@/assets/icons/svg/facebook.svg')"/>
							<span class="text-subtitle vertical-middle">{{nearbyBranch.fb_msg_url}}</span>
						</div>
						<div 
							class="contact-item" 
							v-if="nearbyBranch.line_name" 
							@click="openContact(nearbyBranch.line_name, 'line')"
						>
							<img src="@/assets/icons/thank/line.png" class="icon-contact">
							<span class="text-subtitle vertical-middle">{{nearbyBranch.line_name}}</span>
						</div>
						<div class="contact-item" v-if="nearbyBranch.wlb_comment">
								<img src="@/assets/icons/moreInfo.png" class="icon-contact">
								<span class="text-subtitle--bold">{{ $t('pageRestaurant.moreInfo') }}</span><br>
								<span class="text-subtitle ml--8">{{ nearbyBranch.wlb_comment }}</span>
						</div>
				</div>
			</div>
		</template>
	</Skeleton>
</template>

<script>
// import RestaurantImage from "./RestaurantImage";
import Mixins from '@/utils/mixins'
import queryBuilder from '@/utils/queryBuilder'
import contact from '@/utils/contact'

import Gmap from '@/components/element/Gmap'

export default {    
    components: {
			Gmap
    },
    mixins: [Mixins],
    props:{
			nearbyBranch: { default: null },
			loading: { default: false }
    },
    methods: {
			openContact: contact.openContact,
			toTelephoneNumber: contact.toTelephoneNumber,
    },
    computed: {
			deliveryTime: function() {
				if (!this.nearbyBranch) return {}
				return this.nearbyBranch.deliveryTime || {}
			},
			nextDeliveryTime: function() {
				if (!this.nearbyBranch) return {}
				return this.nearbyBranch.nextDeliveryTime || {}
			},
			branchPosition: function() {
				const lat = Number.parseFloat(this.nearbyBranch.latitude)
				const lng = Number.parseFloat(this.nearbyBranch.longitude)
				const hasPosition = lat && lng
				return hasPosition ? { lat, lng } : null
			},
			googleMapLink: function() {
				const { latitude = '', longitude = '' } = this.nearbyBranch
				const location = latitude + ',' + longitude
				return "https://www.google.com/maps/search/?api=1&query=" + location
			}
    }
}
</script>


// WEBPACK FOOTER //
// src/components/restaurant/RestaurantDetail.vue